import React, { useEffect, useState } from 'react'
import axios from 'axios'
import Cookies from 'js-cookie'
import { useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { ClipLoader } from 'react-spinners'
import API_BASE_URL from '../../config/api'
import './Login.css'

const VERIFY_2FA_URL = `${API_BASE_URL}/api/v1/users/verify-2fa/`
const RESEND_2FA_URL = `${API_BASE_URL}/api/v1/users/resend-2fa/`
const RESEND_COOLDOWN_SECONDS = 60

const Verify2Factor = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [otp, setOtp] = useState('')
    const [loading, setLoading] = useState(false)
    const [resending, setResending] = useState(false)
    const [cooldown, setCooldown] = useState(0)
    const [challengeToken, setChallengeToken] = useState(
        location.state?.challenge_token ||
            sessionStorage.getItem('challenge_token') ||
            ''
    )
    const email = location.state?.email || sessionStorage.getItem('2fa_email') || ''

    useEffect(() => {
        if (!challengeToken) {
            Swal.fire({
                icon: 'warning',
                title: 'Verification required',
                text: 'Please sign in first to receive a verification code.',
                timer: 2500,
                showConfirmButton: false,
            }).then(() => navigate('/', { replace: true }))
        }
    }, [challengeToken, navigate])

    useEffect(() => {
        if (cooldown <= 0) return undefined
        const timerId = setInterval(() => {
            setCooldown((prev) => (prev > 0 ? prev - 1 : 0))
        }, 1000)
        return () => clearInterval(timerId)
    }, [cooldown])

    const handleNormalLogin = (role) => {
        switch (role) {
            case 'overall_manager':
                navigate('/admin/dashboard', { replace: true })
                break
            case 'hr_manager':
            case 'admin_manager':
                navigate('/hr-manager/dashboard', { replace: true })
                break
            case 'immediate_manager':
            case 'md':
                navigate('/manager/dashboard', { replace: true })
                break
            case 'staff':
                navigate('/employee/dashboard', { replace: true })
                break
            default:
                navigate('/', { replace: true })
                break
        }
    }

    const completeLogin = (data) => {
        Cookies.set('token', data.access, { expires: 7, path: '/' })
        Cookies.set('userRole', data.user.role)
        Cookies.set('employeeId', data.user.id)
        Cookies.set('currency', data.company?.currency)
        Cookies.set(
            'subscribedModules',
            JSON.stringify(data.company?.subscribed_modules || []),
            { expires: 7, path: '/' }
        )
        Cookies.set('user', JSON.stringify(data.user), { expires: 7, path: '/' })
        Cookies.set('company_name', data.company?.trade_name)

        sessionStorage.removeItem('challenge_token')
        sessionStorage.removeItem('2fa_email')

        if (data.user.first_login) {
            switch (data.user.role) {
                case 'hr_manager':
                case 'admin_manager':
                    navigate('/hr/change-password', {
                        replace: true,
                        state: { oldPassword: data.user.employee_id },
                    })
                    break
                case 'immediate_manager':
                    navigate('/manager/change-password', {
                        replace: true,
                        state: { oldPassword: data.user.employee_id },
                    })
                    break
                case 'staff':
                    navigate('/employee/change-password', {
                        replace: true,
                        state: { oldPassword: data.user.employee_id },
                    })
                    break
                default:
                    handleNormalLogin(data.user.role)
                    break
            }
        } else {
            handleNormalLogin(data.user.role)
        }
    }

    const handleResendOtp = async () => {
        if (!challengeToken) {
            Swal.fire('Error', 'Missing verification challenge. Please sign in again.', 'error')
            navigate('/', { replace: true })
            return
        }

        if (cooldown > 0 || resending) return

        setResending(true)
        try {
            const response = await axios.post(RESEND_2FA_URL, {
                challenge_token: challengeToken,
                email: email || undefined,
            })

            const newChallengeToken =
                response.data?.challenge_token ||
                response.data?.challengeToken ||
                response.data?.totp_challenge_token

            if (newChallengeToken) {
                setChallengeToken(newChallengeToken)
                sessionStorage.setItem('challenge_token', newChallengeToken)
            }

            setOtp('')
            setCooldown(RESEND_COOLDOWN_SECONDS)

            Swal.fire({
                icon: 'success',
                title: 'OTP sent',
                text: `A new verification code has been sent to ${email || 'your email'}.`,
                timer: 2000,
                showConfirmButton: false,
            })
        } catch (error) {
            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.error ||
                error.response?.data?.message ||
                'Failed to resend OTP. Please try again.'

            Swal.fire({
                icon: 'error',
                title: 'Resend failed',
                text: errorMessage,
            })
        } finally {
            setResending(false)
        }
    }

    const verifyOtp = async (otpValue) => {
        if (loading) return

        if (!challengeToken) {
            Swal.fire('Error', 'Missing verification challenge. Please sign in again.', 'error')
            navigate('/', { replace: true })
            return
        }

        const trimmedOtp = otpValue.trim()
        if (!trimmedOtp) {
            Swal.fire('Error', 'Please enter the OTP sent to your email.', 'error')
            return
        }

        setLoading(true)
        try {
            const response = await axios.post(VERIFY_2FA_URL, {
                challenge_token: challengeToken,
                otp: trimmedOtp,
            })

            Swal.fire({
                icon: 'success',
                title: 'Verified!',
                text: 'Two-factor authentication successful.',
                timer: 1500,
                showConfirmButton: false,
            })

            completeLogin(response.data)
        } catch (error) {
            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.error ||
                error.response?.data?.otp?.[0] ||
                error.response?.data?.message ||
                'Invalid or expired OTP. Please try again.'

            Swal.fire({
                icon: 'error',
                title: 'Verification failed',
                text: errorMessage,
            })
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        verifyOtp(otp)
    }

    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6)
        setOtp(value)
        if (value.length === 6) {
            verifyOtp(value)
        }
    }

    return (
        <div className="login-page">
            <div className="login-bg-orb login-bg-orb--1" aria-hidden="true" />
            <div className="login-bg-orb login-bg-orb--2" aria-hidden="true" />
            <div className="login-bg-orb login-bg-orb--3" aria-hidden="true" />

            <div className="login-card">
                <div className="login-logo-wrap">
                    <div className="login-logo-backdrop">
                        <img
                            src="/assets/images/burray/burray.png"
                            alt="Burray HR"
                            className="login-logo"
                        />
                    </div>
                </div>

                <h1 className="login-title">Verify Identity</h1>
                <p className="login-subtitle">
                    Enter the OTP sent to {email || 'your email'} to continue.
                </p>

                <form onSubmit={handleSubmit}>
                    <div className="login-field">
                        <label htmlFor="otp">One-Time Password (OTP)</label>
                        <input
                            type="text"
                            id="otp"
                            name="otp"
                            value={otp}
                            onChange={handleOtpChange}
                            required
                            placeholder="******"
                            className="login-input"
                            autoComplete="one-time-code"
                            inputMode="numeric"
                            maxLength={6}
                            pattern="\d{6}"
                            autoFocus
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading || resending || !otp.trim()}
                        className="login-submit"
                    >
                        {loading ? (
                            <>
                                <ClipLoader size={18} color="#ffffff" />
                                Verifying…
                            </>
                        ) : (
                            'Verify & Continue'
                        )}
                    </button>

                    <div className="login-forgot-wrap" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <button
                            type="button"
                            onClick={handleResendOtp}
                            disabled={resending || cooldown > 0 || loading}
                            className="login-forgot-btn"
                        >
                            {resending
                                ? 'Sending…'
                                : cooldown > 0
                                    ? `Resend OTP in ${cooldown}s`
                                    : 'Resend OTP'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                sessionStorage.removeItem('challenge_token')
                                sessionStorage.removeItem('2fa_email')
                                navigate('/', { replace: true })
                            }}
                            className="login-forgot-btn"
                            disabled={loading || resending}
                        >
                            Back to Sign In
                        </button>
                    </div>
                </form>

                <p className="login-footer">Powered by Burray Systems</p>
            </div>
        </div>
    )
}

export default Verify2Factor
